/**
 * MembershipForm Component (ORCH-336, ORCH-21 §12; PRD UM-6 to UM-8)
 *
 * Purpose: Everything that decides a person's access in one tenant, in one form:
 *          their roles, their Company there and their security attributes.
 *          Used by the user page's Access Rights section, the invite dialog and
 *          Add to a Tenant, and built to be reused for "members of this tenant"
 *          in the Tenant Manager.
 * Logic: Roles use the shared RoleSelect. The Company and the attributes are
 *        ordinary fields of the `user_tenant` model, rendered by MetadataForm
 *        through the existing list and hierarchy renderers, inside a
 *        PickerTenant (the tenant being managed, which for a system admin in
 *        GLOBAL isn't the signed-in one), scoped to `permission` (passed to each form
 *        as `pickerPermission`, an override of the model default, because the layout
 *        is always mode="edit" while an invite exercises user.create; ORCH-366), so
 *        the pickers offer that tenant's values and only those the caller's
 *        permission reaches.
 *        The Company (CompanyField, ORCH-410 §4.2) offers only the agencies and
 *        advertisers the tenant is enabled for or owns (in GLOBAL, OrchestrateHQ),
 *        which the server lists and checks again on save (ORCH-398). The user
 *        page shows it on the Profile card instead (`showCompany={false}`). An
 *        attribute that narrows another is limited to it: the Media Subtype
 *        picker offers only subtypes of the media types already chosen, and
 *        everything when none is (ORCH-389, useAttributeNarrowing). The form
 *        reports the whole draft upward on every change; the parent decides when
 *        to save.
 * Dependencies: RoleSelect, CompanyField, MetadataForm, PickerTenant, /authz/dimensions
 */

import React, { useCallback, useMemo } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { RoleSelect } from '../common';
import type { RoleOption } from '../common';
import { MetadataForm } from '../metadata-form';
import { PickerTenant, PickerValues } from '../../context/PickerScopeContext';
import { PermissionKey } from '../../constants/permissionKeys';
import { MemberRole } from '../../types/userManagement';
import { COMPANY_FIELD, MEMBERSHIP_MODEL_ID, MembershipDraft } from './membership';
import CompanyField from './CompanyField';
import { useAttributeNarrowing } from './useAttributeNarrowing';
import { fieldGridSx } from '../form-layout/fieldGrid';

export interface MembershipFormProps {
  /** The tenant the membership is in: its fields and values are that tenant's. */
  tenantId: string;
  /** The role catalog, each role flagged `assignable` for the caller. */
  roles: RoleOption[];
  /** Roles the person already holds (kept read-only when the caller can't assign them). */
  heldRoles?: MemberRole[];
  /** The stored membership the form starts from. Memoize it: a new object resets the form. */
  initial: MembershipDraft;
  value: MembershipDraft;
  onChange: (draft: MembershipDraft) => void;
  /** The permission being exercised: `user.edit` on the user page, `user.create` on an invite. */
  permission: PermissionKey;
  /** Without `user.assign_roles` the role field is read-only (PRD GR-1). */
  canAssignRoles: boolean;
  /** The security-attribute fields; the Company is laid out beside the roles. */
  attributeFieldIds: string[];
  readOnly?: boolean;
  /** Without `user.edit` on this member, the Company and attributes are read-only. */
  canEditAttributes?: boolean;
  /** Show the Company under Role(s) (default). The user page shows it on the Profile card instead. */
  showCompany?: boolean;
  /** The server fixes OrchestrateHQ for one of Orchestrate's own people (ORCH-410 Q3). */
  companyLocked?: boolean;
  /** Which roles inherit the person's attributes, named under the heading. */
  attributeUsers?: string[];
  /** Warn when no role is held. Off where nothing has been picked yet (an invite). */
  warnNoRoles?: boolean;
}

const MembershipForm: React.FC<MembershipFormProps> = ({
  tenantId,
  roles,
  heldRoles,
  initial,
  value,
  onChange,
  permission,
  canAssignRoles,
  attributeFieldIds,
  readOnly = false,
  canEditAttributes = true,
  attributeUsers,
  warnNoRoles = true,
  showCompany = true,
  companyLocked = false,
}) => {
  // MetadataForm resets whenever `initialData` changes identity
  const initialData = useMemo(() => ({ metadata: initial.metadata }), [initial.metadata]);

  // Two forms over one model: each reports only the fields it shows
  const mergeFields = useCallback(
    (fieldIds: string[]) => (current: { metadata: Record<string, unknown> }) => {
      const next = { ...value.metadata };
      let changed = false;
      fieldIds.forEach((id) => {
        if (JSON.stringify(next[id] ?? null) !== JSON.stringify(current.metadata[id] ?? null)) {
          next[id] = current.metadata[id];
          changed = true;
        }
      });
      if (changed) onChange({ ...value, metadata: next });
    },
    [value, onChange]
  );

  // ORCH-389: a narrowed attribute offers only what relates to the chosen values
  const narrowing = useAttributeNarrowing(tenantId, value.metadata);

  const noRoles = value.roleIds.length === 0;

  return (
    <PickerTenant tenantId={tenantId}>
      {/* Column 1: Role(s) and Company; columns 2–3: Security Attributes (one shared grid) */}
      <Box sx={fieldGridSx(3)} data-testid="membership-form">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={500}>
            Roles
          </Typography>
          <RoleSelect
            roles={roles}
            held={heldRoles}
            value={value.roleIds}
            onChange={(roleIds) => onChange({ ...value, roleIds })}
            required
            disabled={readOnly || !canAssignRoles}
            helperText={
              !canAssignRoles && !readOnly
                ? "You don't have permission to assign roles."
                : undefined
            }
            dataTestId="membership-roles"
          />
          {noRoles && warnNoRoles && (
            <Alert severity="warning" data-testid="membership-no-roles">
              With no roles, this person can sign in but do nothing.
            </Alert>
          )}
          {showCompany && (
            <CompanyField
              tenantId={tenantId}
              attributeFieldIds={attributeFieldIds}
              initialValue={initial.metadata[COMPANY_FIELD]}
              value={value.metadata[COMPANY_FIELD]}
              onChange={(company) =>
                onChange({ ...value, metadata: { ...value.metadata, [COMPANY_FIELD]: company } })
              }
              permission={permission}
              readOnly={readOnly || !canEditAttributes}
              locked={companyLocked}
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            gridColumn: { xs: 'auto', md: 'span 2' },
            minWidth: 0,
          }}
          data-testid="membership-attributes"
        >
          <Typography variant="subtitle1" fontWeight={500}>
            Security Attributes
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="membership-attributes-hint"
          >
            These grant nothing on their own.{' '}
            {attributeUsers && attributeUsers.length > 0
              ? `Used by ${attributeUsers.join(', ')}.`
              : 'Set them for roles scoped to the person’s own attributes.'}
          </Typography>
          <PickerValues values={narrowing}>
            <MetadataForm
              modelId={MEMBERSHIP_MODEL_ID}
              tenantId={tenantId}
              mode="edit"
              initialData={initialData}
              excludeFields={[COMPANY_FIELD]}
              onChange={mergeFields(attributeFieldIds)}
              sectionTitle=""
              showSectionDivider={false}
              columnLayout="double"
              readOnly={readOnly || !canEditAttributes}
              pickerPermission={permission}
            />
          </PickerValues>
        </Box>
      </Box>
    </PickerTenant>
  );
};

export default MembershipForm;
